import { ListFilter } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type {
  Column,
  ColumnDataType,
  DataTableFilterActions,
  FilterModel,
  FilterStrategy,
  FiltersState,
} from "../core/types";
import type { Locale } from "../lib/i18n";
import { FilterValueController } from "./filter-value";

interface FilterSelectorProps<TData> {
  filters: FiltersState;
  columns: Column<TData>[];
  actions: DataTableFilterActions;
  strategy: FilterStrategy;
  locale?: Locale;
}

export const FilterSelector = memo(__FilterSelector) as typeof __FilterSelector;

function __FilterSelector<TData>({ filters, columns, actions, strategy, locale = "en" }: FilterSelectorProps<TData>) {
  const [open, setOpen] = useState(false);

  const hasFilters = filters.length > 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        render={<Button variant="secondary" size="icon" className={cn(hasFilters && "w-fit !px-2")} />}
      >
        <ListFilter className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        {columns.map((column) => {
          const filter = filters.find((f) => f.columnId === column.id);
          return (
            <FilterColumnSubmenu
              key={column.id}
              column={column as Column<TData, ColumnDataType>}
              filter={filter}
              actions={actions}
              strategy={strategy}
              locale={locale}
              onClose={() => setOpen(false)}
            />
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface FilterColumnSubmenuProps<TData, TType extends ColumnDataType> {
  column: Column<TData, TType>;
  filter: FilterModel<TType> | undefined;
  actions: DataTableFilterActions;
  strategy: FilterStrategy;
  locale?: Locale;
  onClose: () => void;
}

function FilterColumnSubmenu<TData, TType extends ColumnDataType>({
  column,
  filter,
  actions,
  strategy,
  locale = "en",
  onClose,
}: FilterColumnSubmenuProps<TData, TType>) {
  const prefetch = useCallback(() => {
    column.prefetchOptions();
    column.prefetchValues();
    column.prefetchFacetedUniqueValues();
    column.prefetchFacetedMinMaxValues();
  }, [column]);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger onMouseEnter={prefetch} onFocus={prefetch}>
        <column.icon strokeWidth={2.25} className="size-4" />
        <span>{column.displayName}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-56 p-0">
        <FilterValueController
          filter={filter as FilterModel<TType>}
          column={column}
          actions={actions}
          strategy={strategy}
          locale={locale}
          onClose={onClose}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
